import { GovIcon } from '@gov-design-system-ce/react';
import type { DatoveSchrankyDatovaZpravaDto } from '@gov-nx/api';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import {
	DataBoxMessageAttachmentProvider,
	useDataBoxMessageDetailContext,
} from '@gov-nx/module/data-box';
import { PageCode } from '@gov-nx/module/page';
import { Button, DocumentDownloadContainer, LayoutGap } from '@gov-nx/ui/web';
import { useWindowDimensions } from '@gov-nx/utils/web';
import { DataBoxAttachmentItem } from './DataBoxAttachmentItem';
import { useExtraConfigContext } from '@gov-nx/core/app';

const MOBILE_WIDTH_MAX = 768;

interface DataBoxAttachmentListProps {
	message: DatoveSchrankyDatovaZpravaDto;
}

export const DataBoxAttachmentList = ({
	message,
}: DataBoxAttachmentListProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tn = getLocalizeCurried(PageCode['datove-schranky']);
	const {
		extraConfig: {
			pageRoutes: {
				databoxConfig: { isSaveToOwnDocumentsAllowed },
			},
		},
	} = useExtraConfigContext();

	const {
		folderType,
		showAllAttachments,
		setShowAllAttachments,
		dataBoxId,
		messageId,
		messageOperations: { handleSaveAllAttachmentsToDocuments },
		loadingItems,
	} = useDataBoxMessageDetailContext();

	const { width } = useWindowDimensions();
	const maxItemsCount = width < MOBILE_WIDTH_MAX ? 2 : 4;

	const items = message.prilohy;
	const limitedItems = showAllAttachments
		? items
		: items?.slice(0, maxItemsCount);

	return (
		<section className="p-(--templates-margin-l) border-t border-(--border-subtlest) text-center bg-(--background-block-primary) [ xl:text-left ]">
			<LayoutGap
				gap="l"
				className="mt-0!">
				<DocumentDownloadContainer label={tn('prilohy.prilohy')}>
					{limitedItems?.map((attachment) => (
						<DataBoxMessageAttachmentProvider
							key={attachment.hashPrilohy as string}
							folderType={folderType}
							dataBoxId={dataBoxId}
							messageId={messageId}>
							<DataBoxAttachmentItem attachment={attachment} />
						</DataBoxMessageAttachmentProvider>
					))}
				</DocumentDownloadContainer>

				{(items ?? [])?.length > maxItemsCount && !showAllAttachments && (
					<Button
						color="primary"
						type="base"
						size="s"
						isExpandedMobile
						onClick={() => setShowAllAttachments(true)}
						aria-label={tn('prilohy.zobrazit-dalsi', {
							pocet: (items ?? [])?.length - maxItemsCount,
						})}
						iconStart={
							<GovIcon
								name="plus-lg"
								type="templates"
							/>
						}>
						<span>
							{tn('prilohy.zobrazit-dalsi', {
								pocet: (items ?? [])?.length - maxItemsCount,
							})}
						</span>
					</Button>
				)}

				{isSaveToOwnDocumentsAllowed && (
					<Button
						color="primary"
						type="base"
						size="s"
						isExpandedMobile
						isLoading={loadingItems.saveAttachmentsToDocuments}
						onClick={() => handleSaveAllAttachmentsToDocuments(message)}
						isDisabled={!!loadingItems.saveAttachmentsToDocuments}
						aria-label={tn('prilohy.ulozit-prilohy')}
						iconStart={
							!loadingItems.saveAttachmentsToDocuments && (
								<GovIcon
									name="floppy"
									type="templates"
								/>
							)
						}>
						{tn('prilohy.ulozit-prilohy')}
					</Button>
				)}
			</LayoutGap>
		</section>
	);
};
